import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, Globe, Zap, Mic, Server, 
  Database, Activity, Clock, CheckCircle2,
  Shield, PlayCircle, BarChart3, Wifi,
  User, Hash, ArrowRight, XCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const InsuranceCallingSlide = () => {
  const [activeStep, setActiveStep] = useState(0);

  // Simulated conversation for the graphic
  const conversation = [
    { role: 'ai', text: "Hi, I'm calling to verify history for procedure D4341.", time: "0:05" },
    { role: 'rep', text: "One moment please... okay, last date of service was 01/15/2022.", time: "2:15" },
    { role: 'ai', text: "Thank you. Does that apply to all 4 quadrants?", time: "2:20" },
    { role: 'rep', text: "Yes, all quadrants are eligible again.", time: "2:35" }
  ];

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2" />

        {/* Floating Agent Header (Top Left) - Dark Theme */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="flex -space-x-4">
                 <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm z-10 relative">
                     <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
                 </div>
                 <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm z-0 relative">
                     <img src="https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png" alt="Laura" className="w-full h-full object-cover" />
                 </div>
            </div>
            <div className="text-left pl-2">
                <h3 className="text-lg font-bold text-white leading-none">Amanda & Laura</h3>
                <p className="text-xs text-indigo-400 font-bold uppercase tracking-wide">Insurance Team</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-6">
                
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    Amanda comes with a teammate, <span className="text-indigo-400">Laura</span>, the AI insurance caller.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                    Laura can call insurance, wait on hold, speak to a representative, and get benefits and information like procedure histories that you otherwise are not able to get online.
                </p>
            </div>

            {/* Right Side: Visualization */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="relative w-full max-w-4xl h-[600px] bg-slate-950 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden flex">
                    
                    {/* Visualizer Column */}
                    <div className="w-1/3 bg-slate-900 border-r border-slate-800 flex flex-col items-center justify-center relative overflow-hidden p-6">
                        {/* Active Call Waveform Animation */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-20">
                             {[1,2,3].map(i => (
                                 <motion.div 
                                    key={i}
                                    className="absolute border border-indigo-500 rounded-full"
                                    animate={{ 
                                        width: ["100px", "400px"],
                                        height: ["100px", "400px"],
                                        opacity: [1, 0]
                                    }}
                                    transition={{ 
                                        duration: 2, 
                                        repeat: Infinity,
                                        delay: i * 0.6,
                                        ease: "easeOut"
                                    }}
                                 />
                             ))}
                        </div>

                        <div className="relative z-10 text-center">
                            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/30 mb-6 mx-auto">
                                <Phone className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Calling MetLife...</h3>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-mono text-indigo-400">
                                <Activity className="w-3 h-3" />
                                02:45
                            </div>
                            
                            <div className="mt-8 space-y-2 w-full">
                                <div className="text-xs text-slate-500 uppercase tracking-wider text-left pl-2 mb-2">Live Transcript</div>
                                {conversation.map((line, i) => (
                                    <motion.div 
                                        key={i}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.5 }}
                                        className={`p-3 rounded-xl text-xs text-left ${
                                            line.role === 'ai' 
                                                ? 'bg-indigo-500/10 border border-indigo-500/20 text-indigo-200' 
                                                : 'bg-slate-800 border border-slate-700 text-slate-300'
                                        }`}
                                    >
                                        <span className="font-bold opacity-70 block text-[10px] mb-0.5">{line.role === 'ai' ? 'AI AGENT' : 'PAYER REP'}</span>
                                        {line.text}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Data Extraction Column */}
                    <div className="flex-1 bg-slate-950 p-8 flex flex-col">
                        {/* Call Details Header */}
                        <div className="flex flex-col gap-1 mb-6 border-b border-slate-800 pb-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                   <Phone className="w-4 h-4 text-indigo-400" /> Call Verified
                                </h3>
                                <div className="text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer flex items-center gap-1">
                                    View Transcript <ArrowRight className="w-3 h-3" />
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400 mt-2">
                                <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Rep: <span className="text-slate-300">Sarah</span></span>
                                <span className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" /> Ref: <span className="text-slate-300">88291-AB</span></span>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="space-y-4 overflow-y-auto flex-1 pr-1 custom-scrollbar">
                            
                            {/* Benefits Summary Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-2">
                                 <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800/60">
                                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Deductible Rem.</div>
                                      <div className="text-sm font-bold text-white">$25.00</div>
                                 </div>
                                 <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800/60">
                                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Annual Max Rem.</div>
                                      <div className="text-sm font-bold text-white">$1,250.00</div>
                                 </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                     <h4 className="font-bold text-slate-200 text-sm">Procedure History</h4>
                                     <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] h-5 px-2">Verified via Phone</Badge>
                                </div>

                                {/* Table/List of procedures */}
                                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                                     <Table>
                                        <TableHeader className="bg-slate-950">
                                            <TableRow className="border-slate-800 hover:bg-transparent">
                                                <TableHead className="text-slate-500 h-8 text-xs font-medium w-[80px]">Code</TableHead>
                                                <TableHead className="text-slate-500 h-8 text-xs font-medium">Last Date</TableHead>
                                                <TableHead className="text-slate-500 h-8 text-xs font-medium">Freq</TableHead>
                                                <TableHead className="text-slate-500 h-8 text-xs font-medium text-right">Eligible</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {[
                                                { code: "D0150", desc: "Comp Exam", last: "01/15/2019", freq: "1 in 5y", eligible: true },
                                                { code: "D0210", desc: "FMX", last: "06/10/2021", freq: "1 in 3y", eligible: true },
                                                { code: "D4341", desc: "SRP (UR)", last: "02/12/2020", freq: "1 in 2y", eligible: true },
                                                { code: "D2740", desc: "Crown", last: "05/01/2018", freq: "1 in 5y", eligible: true },
                                                { code: "D4910", desc: "Perio Maint", last: "08/15/2023", freq: "4 in 1y", eligible: true },
                                                { code: "D1110", desc: "Prophy", last: "11/15/2023", freq: "2 in 1y", eligible: true },
                                            ].map((proc, i) => (
                                                <TableRow key={i} className="border-slate-800 hover:bg-slate-800/50 transition-colors">
                                                    <TableCell className="font-medium text-slate-200 py-2.5 text-xs">
                                                        <div className="font-bold text-indigo-300">{proc.code}</div>
                                                        <div className="text-[10px] text-slate-500 truncate max-w-[80px]">{proc.desc}</div>
                                                    </TableCell>
                                                    <TableCell className="text-slate-300 py-2.5 text-xs">{proc.last}</TableCell>
                                                    <TableCell className="text-slate-400 py-2.5 text-xs">{proc.freq}</TableCell>
                                                    <TableCell className="text-right py-2.5 text-xs">
                                                        {proc.eligible ? (
                                                            <div className="inline-flex items-center justify-end gap-1.5 text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded-md">
                                                                <CheckCircle2 className="w-3 h-3" /> Yes
                                                            </div>
                                                        ) : (
                                                            <div className="inline-flex items-center justify-end gap-1.5 text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded-md">
                                                                <XCircle className="w-3 h-3" /> No
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                     </Table>
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-slate-900 text-center">
                            <p className="text-[10px] text-slate-500 flex items-center justify-center gap-2">
                                <Database className="w-3 h-3" />
                                Automatically synced to patient file in PMS
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default InsuranceCallingSlide;
